import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { workflowApi, skillApi, toolApi, eventConnectorApi, type WorkflowDefinition, type WorkflowExecution } from '@/lib/api';
import {
  Zap, ArrowLeft, Circle, Play, ChevronRight, Activity, Clock, Cpu, Bot, CheckCircle2, XCircle, AlertTriangle, Timer
} from 'lucide-react';
import { WorkflowCanvas } from '@/components/workflow/WorkflowCanvas';
import styles from './_auth.workflows.detail.module.css';

export const Route = createFileRoute('/_auth/workflows/$workflowId')({
  component: WorkflowDetailPage,
});

type Tab = 'ongoing' | 'design' | 'history';

const ONGOING_STATUSES = new Set(['RUNNING', 'WAITING_CALLBACK', 'WAITING_APPROVAL', 'PENDING']);

/** Format seconds into a human-readable duration string */
function fmtSec(sec: number | undefined | null): string {
  if (sec == null) return '—';
  if (sec < 60) return `${sec.toFixed(1)}s`;
  const m = Math.floor(sec / 60);
  const s = (sec % 60).toFixed(0).padStart(2, '0');
  return `${m}m ${s}s`;
}

/** Get token breakdown lines */
function getTokenBreakdown(tok: Record<string, number> | undefined): { model: string; tokens: number }[] {
  if (!tok) return [];
  return Object.entries(tok).map(([model, tokens]) => ({ model, tokens }));
}

/** Live running clock — increments every second from startedAt */
function LiveClock({ startedAt }: { startedAt?: string }) {
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!startedAt) return;
    const base = Date.now() - new Date(startedAt).getTime();
    setElapsed(Math.floor(base / 1000));
    intervalRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
    }, 1000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [startedAt]);

  if (!startedAt) return <span>—</span>;
  const m = Math.floor(elapsed / 60);
  const s = (elapsed % 60).toString().padStart(2, '0');
  return (
    <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--color-brand)', fontWeight: 600 }}>
      {m > 0 ? `${m}m ` : ''}{s}s
    </span>
  );
}

/** Status badge with icon */
function StatusBadge({ status }: { status: string }) {
  const isOngoing = ONGOING_STATUSES.has(status);
  const isSuccess = status === 'COMPLETED';
  const isFailed = status === 'FAILED' || status === 'CANCELLED';
  const color = isOngoing ? 'var(--color-brand)' : isSuccess ? '#22c55e' : isFailed ? '#ef4444' : 'var(--color-text-muted)';
  const Icon = isOngoing ? Activity : isSuccess ? CheckCircle2 : isFailed ? XCircle : AlertTriangle;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: '20px',
      background: `${color}18`, color,
      fontSize: '0.72rem', fontWeight: 700, border: `1px solid ${color}30`,
    }}>
      <Icon size={11} />
      {status.replace(/_/g, ' ')}
    </span>
  );
}

/** Ongoing execution row */
function OngoingRow({ exec, totalSteps }: { exec: WorkflowExecution; totalSteps: number }) {
  const tokenBreakdown = getTokenBreakdown(exec.tokensUsedByModel as Record<string, number> | undefined);
  const totalTokens = tokenBreakdown.reduce((a, b) => a + b.tokens, 0);
  return (
    <div style={{
      background: 'var(--color-surface-2)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-md)',
      padding: '1rem 1.25rem',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
      gap: '1rem',
      alignItems: 'start',
    }}>
      {/* ID + status */}
      <div>
        <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginBottom: 4 }}>EXECUTION</div>
        <Link
          to="/workflows/executions/$executionId"
          params={{ executionId: exec.id }}
          style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'var(--color-brand)' }}
        >
          {exec.id.slice(0, 8)}…
        </Link>
        <div style={{ marginTop: 6 }}><StatusBadge status={exec.status} /></div>
      </div>

      {/* Step progress */}
      <div>
        <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginBottom: 4 }}>STEP PROGRESS</div>
        <div style={{ fontSize: '0.9rem', fontWeight: 700 }}>
          {exec.currentStepIndex + 1}
          {totalSteps > 0 && <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}> / {totalSteps}</span>}
        </div>
        {totalSteps > 0 && (
          <div style={{ marginTop: 6, height: 4, background: 'var(--color-border)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 2,
              width: `${Math.min(100, ((exec.currentStepIndex + 1) / totalSteps) * 100)}%`,
              background: 'var(--color-brand)',
              transition: 'width 0.3s ease',
            }} />
          </div>
        )}
        <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginTop: 4 }}>
          Started {exec.startedAt ? new Date(exec.startedAt).toLocaleTimeString() : '—'}
        </div>
      </div>

      {/* Wall clock */}
      <div>
        <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
          <Timer size={10} /> ELAPSED
        </div>
        <div style={{ fontSize: '0.95rem', fontWeight: 700 }}>
          <LiveClock startedAt={exec.startedAt} />
        </div>
        {exec.activeDurationSec != null && (
          <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginTop: 4 }}>
            Active: {fmtSec(exec.activeDurationSec)}
          </div>
        )}
      </div>

      {/* Function CPU time */}
      <div>
        <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
          <Cpu size={10} /> FUNCTION TIME
        </div>
        <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>
          {fmtSec(exec.functionDurationSec)}
        </div>
        <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginTop: 4 }}>
          Excluding wait/callback
        </div>
      </div>

      {/* Token usage */}
      <div>
        <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
          <Bot size={10} /> LLM TOKENS
        </div>
        <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>
          {totalTokens > 0 ? totalTokens.toLocaleString() : '—'}
        </div>
        {tokenBreakdown.length > 0 && (
          <div style={{ marginTop: 4 }}>
            {tokenBreakdown.map(({ model, tokens }) => (
              <div key={model} style={{ fontSize: '0.68rem', color: 'var(--color-text-muted)' }}>
                {model.length > 20 ? model.slice(0, 18) + '…' : model}: {tokens.toLocaleString()}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function WorkflowDetailPage() {
  const { workflowId } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [tab, setTab] = useState<Tab>('ongoing');

  // ── Data queries ────────────────────────────────────────────────────
  const { data: workflow, isLoading } = useQuery({
    queryKey: ['workflows', workflowId],
    queryFn: () => workflowApi.get(workflowId),
    // Poll while on the ongoing tab
    refetchInterval: tab === 'ongoing' ? 5000 : false,
  });

  const { data: allTools = [] } = useQuery({
    queryKey: ['tools'],
    queryFn: toolApi.list,
    staleTime: 60_000,
  });

  const { data: allSkills = [] } = useQuery({
    queryKey: ['skills'],
    queryFn: skillApi.list,
    staleTime: 60_000,
  });

  const { data: allConnectors = [] } = useQuery({
    queryKey: ['eventConnectors'],
    queryFn: eventConnectorApi.list,
    staleTime: 60_000,
  });

  // ── Mutations ────────────────────────────────────────────────────────
  const updateMutation = useMutation({
    mutationFn: (body: Partial<WorkflowDefinition>) =>
      workflowApi.update(workflowId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflows', workflowId] });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (active: boolean) => workflowApi.update(workflowId, { active }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflows', workflowId] });
    },
  });

  const executeMutation = useMutation({
    mutationFn: () => workflowApi.execute(workflowId),
    onSuccess: () => {
      // Switch to ongoing tab after triggering a run
      setTab('ongoing');
      qc.invalidateQueries({ queryKey: ['workflows', workflowId] });
    },
  });

  // ── Handlers ─────────────────────────────────────────────────────────
  function handleSaveWorkflow(steps: object[], triggerConfig: any, abortListeners: any[], maxExecutionTime: number | null) {
    updateMutation.mutate({
      steps: steps as WorkflowDefinition['steps'],
      triggerConfig,
      abortListeners,
      maxExecutionTime,
    });
  }

  // ── Loading / error ───────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className={styles.page} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Circle size={14} style={{ animation: 'spin 1s linear infinite', opacity: 0.5 }} />
        Loading workflow…
      </div>
    );
  }

  if (!workflow) {
    return (
      <div className={styles.page}>
        <h2>Workflow not found</h2>
        <button onClick={() => navigate({ to: '/workflows' })} className={styles.primaryBtn}>
          ← Back to Workflows
        </button>
      </div>
    );
  }

  // Prepare tool + skill lists for the canvas
  const availableTools = (allTools as any[]).map((t) => ({
    toolName: t.toolName,
    displayName: t.displayName,
    inputSchema: t.inputSchema,
  }));

  const availableSkills = (allSkills as any[]).map((s) => ({
    id: s.id,
    name: s.name,
    inputSchema: s.inputSchema,
  }));

  // Split executions
  const allExecs = workflow.executions ?? [];
  const ongoingExecs = allExecs.filter((e) => ONGOING_STATUSES.has(e.status));
  const historyExecs = allExecs.filter((e) => !ONGOING_STATUSES.has(e.status));
  const totalSteps = workflow.steps?.length ?? 0;

  return (
    <div className={styles.page}>
      {/* ── Page header ─────────────────────────────────────────────── */}
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>
            <button
              onClick={() => navigate({ to: '/workflows' })}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}
              aria-label="Back to workflows"
            >
              <ArrowLeft size={20} style={{ opacity: 0.6 }} />
            </button>
            <Zap size={22} style={{ color: 'var(--color-brand)' }} />
            {workflow.name}
          </h1>

          <div className={styles.metaRow}>
            <span>
              AI Agent: <span className={styles.agentBadge} style={!workflow.agent ? { background: '#47556920', color: '#94a3b8', border: '1px dashed #47556940' } : {}}>{workflow.agent?.name ?? 'Reusable (No Agent)'}</span>
            </span>
            <span>Version: {workflow.version}</span>
            <span>
              Status:
              <span
                className={styles.statusPill}
                style={{
                  '--dot-color': workflow.active ? 'var(--color-success)' : 'var(--color-muted)',
                  marginLeft: '0.35rem',
                } as React.CSSProperties}
                onClick={() => toggleActiveMutation.mutate(!workflow.active)}
              >
                <Circle size={7} fill="currentColor" />
                {workflow.active ? 'Active' : 'Inactive'}
              </span>
            </span>
            {workflow.maxExecutionTime != null && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                <Clock size={12} />
                Max: {workflow.maxExecutionTime}s
              </span>
            )}
          </div>
        </div>

        <div className={styles.headerActions}>
          <button
            className={styles.primaryBtn}
            onClick={() => executeMutation.mutate()}
            disabled={executeMutation.isPending}
          >
            <Play size={14} fill="currentColor" />
            {executeMutation.isPending ? 'Running…' : 'Run Test'}
          </button>
        </div>
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────── */}
      <div className={styles.tabs}>
        <button
          id="wf-tab-ongoing"
          className={`${styles.tab} ${tab === 'ongoing' ? styles.tabActive : ''}`}
          onClick={() => setTab('ongoing')}
        >
          <Activity size={13} style={{ marginRight: 5 }} />
          Ongoing Workflows
          {ongoingExecs.length > 0 && (
            <span style={{
              marginLeft: 6, background: 'var(--color-brand)', color: '#fff',
              fontSize: '0.65rem', fontWeight: 800, padding: '1px 5px', borderRadius: 10,
            }}>
              {ongoingExecs.length}
            </span>
          )}
        </button>
        <button
          id="wf-tab-design"
          className={`${styles.tab} ${tab === 'design' ? styles.tabActive : ''}`}
          onClick={() => setTab('design')}
        >
          Visual Designer
        </button>
        <button
          id="wf-tab-history"
          className={`${styles.tab} ${tab === 'history' ? styles.tabActive : ''}`}
          onClick={() => setTab('history')}
        >
          Execution History
        </button>
      </div>

      {/* ── Tab content ──────────────────────────────────────────────── */}

      {/* ── ONGOING TAB ─────────────────────────────────────────────── */}
      {tab === 'ongoing' && (
        <div style={{ padding: '0.5rem 0' }}>
          {ongoingExecs.length === 0 ? (
            <div className={styles.emptyExecs}>
              <Activity size={36} style={{ opacity: 0.2, marginBottom: '0.75rem' }} />
              <h3>No active executions</h3>
              <p>Ongoing workflows will appear here in real time. Use <strong>Run Test</strong> above to start one.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {/* Summary bar */}
              <div style={{
                display: 'flex', gap: '1.5rem', padding: '0.75rem 1.25rem',
                background: 'var(--color-surface-2)', borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border)', fontSize: '0.8rem',
              }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Activity size={14} style={{ color: 'var(--color-brand)' }} />
                  <strong>{ongoingExecs.length}</strong> active
                </span>
                {workflow.maxExecutionTime != null && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--color-text-muted)' }}>
                    <Clock size={13} />
                    Timeout limit: <strong>{workflow.maxExecutionTime}s</strong>
                  </span>
                )}
                <span style={{ color: 'var(--color-text-muted)' }}>Auto-refreshes every 5s</span>
              </div>

              {ongoingExecs.map((exec) => (
                <OngoingRow key={exec.id} exec={exec} totalSteps={totalSteps} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── DESIGN TAB ──────────────────────────────────────────────── */}
      {tab === 'design' && (
        <WorkflowCanvas
          initialSteps={(workflow.steps as unknown[]) ?? []}
          initialTriggerConfig={workflow.triggerConfig}
          initialAbortListeners={workflow.abortListeners}
          initialMaxExecutionTime={workflow.maxExecutionTime}
          availableTools={availableTools}
          availableSkills={availableSkills}
          availableConnectors={allConnectors}
          onSave={handleSaveWorkflow}
          isSaving={updateMutation.isPending}
        />
      )}

      {/* ── HISTORY TAB ─────────────────────────────────────────────── */}
      {tab === 'history' && (
        <div>
          {historyExecs.length === 0 ? (
            <div className={styles.emptyExecs}>
              <Zap size={36} style={{ opacity: 0.2, marginBottom: '0.75rem' }} />
              <h3>No completed executions found</h3>
              <p>Trigger a run to see history here.</p>
            </div>
          ) : (
            <table className={styles.executionsTable}>
              <thead>
                <tr>
                  <th>Execution ID</th>
                  <th>Status</th>
                  <th>Steps</th>
                  <th>Started At</th>
                  <th style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Clock size={12} /> Active Time
                  </th>
                  <th style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Cpu size={12} /> Fn Time
                  </th>
                  <th style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Bot size={12} /> Tokens
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {historyExecs.map((exec) => {
                  const tok = exec.tokensUsedByModel as Record<string, number> | undefined;
                  const totalTok = tok ? Object.values(tok).reduce((a, b) => a + b, 0) : 0;
                  return (
                    <tr key={exec.id}>
                      <td>
                        <Link
                          to="/workflows/executions/$executionId"
                          params={{ executionId: exec.id }}
                          className={styles.execId}
                        >
                          {exec.id.slice(0, 8)}…
                        </Link>
                      </td>
                      <td>
                        <StatusBadge status={exec.status} />
                      </td>
                      <td>Step {exec.currentStepIndex + 1}{totalSteps > 0 ? ` / ${totalSteps}` : ''}</td>
                      <td>{exec.startedAt ? new Date(exec.startedAt).toLocaleString() : 'N/A'}</td>
                      <td>{fmtSec(exec.activeDurationSec)}</td>
                      <td>{fmtSec(exec.functionDurationSec)}</td>
                      <td>
                        {totalTok > 0 ? (
                          <span title={tok ? Object.entries(tok).map(([m, t]) => `${m}: ${t}`).join('\n') : ''}>
                            {totalTok.toLocaleString()}
                          </span>
                        ) : '—'}
                      </td>
                      <td>
                        <Link
                          to="/workflows/executions/$executionId"
                          params={{ executionId: exec.id }}
                          className={styles.viewLink}
                        >
                          View <ChevronRight size={14} />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
